import React, {useContext, useEffect, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {v4 as uuidv4} from "uuid";
import {AgentContext} from "../../context/agent-context.jsx";
import GenericEditRemoveCellRenderer from "./generic-edit-remove-cell-renderer.jsx";
import CellRendererAttach from "../../utils/cell-renderer-attach.jsx";
import ConfirmationDeleteList from "../../../../../../components/utils/confirmation-delete-lists.jsx";
import {FaPlus} from "react-icons/fa";
import Swal from "sweetalert2";
import projectUtils from "../../../../../../utils/project-utils.js";
import Button from "../../../../../../components/button/button.jsx";
import {Modal} from "../../../../../../components/modal/modal.jsx";
import InputDatetime from "../../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import Attach from "../../../../../../components/attach/attach.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {format} from "date-fns";

const ConductHistory = () => {
    const generateInitialData = () => ({
        itemId: uuidv4(),
        date: "",
        action: null,
        observation: "",
        conductHistoryFile: ""
    });

    const gridRef = useRef();
    const {setUser, user, setTablesAttachs, tablesAttachs, setChanges, setTabSelected, setCpfFocus} = useContext(AgentContext)
    const [data, setData] = useState(generateInitialData)
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [removedFile, setRemovedFile] = useState(null);

    useEffect(() => {
        if (open) {
            const attachFind = tablesAttachs.find(item => item.name?.includes(`conduct-history-${data.itemId}`));
            if (attachFind) setFile(attachFind);
        }
    }, [open]);

    const remove = (data) => {
        ConfirmationDeleteList(() => {
            setUser(prev => ({
                ...prev,
                conductHistory: prev.conductHistory.filter(dep => dep.itemId !== data.itemId)
            }));

            setTablesAttachs((prevItems) => prevItems.filter((item) => !item.name.includes(`conduct-history-${data.itemId}`)))
            setChanges('conduct_history');
        })
    }

    const [columnDefs] = useState([
        { headerName: "Ação",  field: "action",
            valueFormatter: ({value}) => {
                const action = optionsUtils.actions.find(option => option.value === value)
                if (action) return action.label;

                return null;
            }, comparator: aggridUtils.nullLastComparator
        },
        { headerName: "Data",  field: "date", sort: "desc",
            valueFormatter: ({value}) => {
                if (value) return projectUtils.formatDate(value + 'T00:00:00', 'DD/MM/YYYY');

                return null;
            }, comparator: aggridUtils.nullLastComparator
        },
        { headerName: "Observação",  field: "observation", flex: 1, comparator: aggridUtils.nullLastComparator },

        { headerName: "Anexo",  field: "", width: 60,
            cellRenderer: ({data}) => {
                const {conductHistoryFile, itemId} = data;
                return <CellRendererAttach attachId={conductHistoryFile} itemId={itemId} target={"conduct-history"}/>
            }, comparator: aggridUtils.nullLastComparator
        },
        { headerName: "",  field: "", width: 100,
            cellRenderer: GenericEditRemoveCellRenderer,
            cellRendererParams: {setData, setOpen, remove}
        },
    ]);

    const handleInputs = ({target: {value, id}}) => setData(prev => ({...prev, [id]: value}));

    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        setData(prev => ({...prev, [id]: dateFormated}))
    }

    const save = () => {
        setUser(prevItems => {
            const exists = prevItems.conductHistory.some(t => t.itemId === data.itemId);

            const updatedTrainings = exists
                ? prevItems.conductHistory.map(t => (t.itemId === data.itemId ? data : t))
                : [...prevItems.conductHistory, data];

            return {
                ...prevItems,
                conductHistory: updatedTrainings
            }
        });

        if (file) {
            setTablesAttachs(prevItems => {
                const exists = prevItems.some(item => item.name === file.name);
                return exists ? prevItems.map(item => item.name === file.name ? file : item) : [...prevItems, file];
            });
        }

        if (removedFile) setTablesAttachs((prevItems) => prevItems.filter((item) => item.name !== removedFile.name));
        setChanges('conduct_history');

        onHide();
    }

    const onHide = () => {
        setData(generateInitialData());
        setOpen(false)
        setFile(null)
        setRemovedFile(null)
    }

    const onChangeFile = (uploadedFile) => {
        if (uploadedFile) setFile(uploadedFile);
    }

    const removeFile = (fileUploaded) => {
        if (fileUploaded) {
            setRemovedFile(fileUploaded);

            const target = "conductHistoryFile"
            setData(prev => ({...prev, [target]: null}));
        }
    }
    const handleClick = () => {
        if (!user.general.cpf){
            Swal.fire({
                title: "Atenção",
                text: "Informe o CPF para incluir o histórico de conduta.",
                icon: "warning",
                confirmButtonText: "Continuar",
                confirmButtonColor: "#063A6C",
                cancelButtonText: "Cancelar",
                showCancelButton: true,
                allowOutsideClick: false,
                allowEscapeKey: false,
            }).then(({isConfirmed}) => {
                if (isConfirmed) {
                    setTabSelected("general")
                    setCpfFocus(true)
                }
            });
        }else{
            setOpen(true)
        }
    }

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>Histórico de conduta</Card.Title>
                    <Button className={"h-[30px] w-[30px] !p-0"} onClick={handleClick}>
                        <FaPlus />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className={"h-[calc(100vh-196px)]"}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={user.conductHistory}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal overflow={true} isCloseClick={false} open={open} setOpen={onHide} title={data.itemId ? "Editar histórico de conduta" : "Adicionar histórico de conduta"} className={"w-4/12"}>
                <Modal.Body className="p-2">
                    <div className="grid grid-cols-12 gap-4 mb-3">
                        <div className={"col-span-6"}>
                            <InputDatetime
                                id={"date"}
                                selected={data.date}
                                label={"Data"}
                                dateFormat={"L"}
                                onChange={(date) => handleDate("date", date)}
                            />
                        </div>
                        <div className="col-span-6">
                            <SelectComponent
                                id="action"
                                value={optionsUtils.actions.find(option => option.value === data.action)}
                                options={optionsUtils.actions}
                                label={"Ação"}
                                onChange={(selected) => setData(prev => ({...prev, action: selected?.value}))}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12">
                            <InputForm id="observation" value={data.observation} label="Observação" onChange={handleInputs}/>
                        </div>
                        <div className="col-span-3">
                            <Attach onChangeFile={onChangeFile} removeFile={removeFile}
                                    itemId={`conduct-history-${data.itemId}`} attachedFile={file} attachId={data.conductHistoryFile} />
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-end"}>
                        <Button onClick={save}>
                            Confirmar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ConductHistory;